import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Menu, X, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import MicahAssistant from "./MicahAssistant";
import { SITE } from "../mock";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Testing Services" },
  { to: "/scores", label: "Test Scores" },
  { to: "/pricing", label: "Pricing" },
  {
    label: "Solutions",
    children: [
      { to: "/app-development", label: "App Development" },
      { to: "/blue-ledger", label: "Blue Ledger Accounting" },
    ],
  },
  { to: "/faqs", label: "FAQs" },
  { to: "/my-booking", label: "My Booking" },
  { to: "/contact", label: "Contact" },
];

const Logo = () => (
  <Link to="/" className="flex items-center gap-3 group" data-testid="site-logo">
    <img
      src="/mbs-logo.png"
      alt="Modern Business Solutions logo"
      className="h-14 w-auto transition-transform duration-300 group-hover:scale-105"
    />
    <div className="leading-tight hidden xl:block max-w-[150px]">
      <div className="text-[11px] text-[#0E9AAA] font-semibold tracking-wide uppercase">
        TOEIC® Testing Center • Cayman Islands
      </div>
    </div>
  </Link>
);

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setSolutionsOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top info bar */}
      <div className="bg-[#0A2540] text-white text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-[#39C1D0]" />
            <span>{SITE.address} — {SITE.addressNote}</span>
          </div>
          <div className="flex items-center gap-6">
            <a href={`tel:${SITE.phone}`} className="flex items-center gap-2 hover:text-[#39C1D0] transition-colors">
              <Phone size={14} className="text-[#39C1D0]" /> {SITE.phone}
            </a>
            <a href={`mailto:${SITE.email}`} className="hidden sm:flex items-center gap-2 hover:text-[#39C1D0] transition-colors">
              <Mail size={14} className="text-[#39C1D0]" /> {SITE.email}
            </a>
            <div className="hidden md:flex items-center gap-2">
              <Clock size={14} className="text-[#39C1D0]" /> {SITE.hours}
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[76px] flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label} className="relative group">
                  <button className="px-3 py-2 rounded-md text-sm font-semibold text-slate-700 hover:text-[#0E9AAA] hover:bg-slate-50 flex items-center gap-1 transition-colors">
                    {link.label} <ChevronDown size={14} />
                  </button>
                  <div className="absolute top-full left-0 pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 z-50">
                    <div className="bg-white rounded-xl shadow-xl border border-slate-100 py-2 w-60">
                      {link.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          className="block px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-[#0E9AAA] transition-colors"
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                      isActive
                        ? "text-[#0E9AAA] bg-[#E8F7F9]"
                        : "text-slate-700 hover:text-[#0E9AAA] hover:bg-slate-50"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              )
            )}
            <Link to="/book" className="ml-3">
              <Button
                data-testid="nav-book-btn"
                className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg px-6 shadow-md hover:shadow-lg transition-shadow"
              >
                Book a Test
              </Button>
            </Link>
          </nav>
          <button
            className="lg:hidden p-2 text-[#0A2540]"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg" data-testid="mobile-nav">
            <div className="px-4 py-3 space-y-1">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.label}>
                    <button
                      onClick={() => setSolutionsOpen(!solutionsOpen)}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-semibold text-slate-700"
                    >
                      {link.label}
                      <ChevronDown size={14} className={`transition-transform ${solutionsOpen ? "rotate-180" : ""}`} />
                    </button>
                    {solutionsOpen &&
                      link.children.map((c) => (
                        <NavLink key={c.to} to={c.to} className="block pl-7 pr-3 py-2.5 text-sm font-medium text-slate-600">
                          {c.label}
                        </NavLink>
                      ))}
                  </div>
                ) : (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className={({ isActive }) =>
                      `block px-3 py-2.5 rounded-md text-sm font-semibold ${
                        isActive ? "text-[#0E9AAA] bg-[#E8F7F9]" : "text-slate-700"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                )
              )}
              <Link to="/book" className="block pt-2">
                <Button className="w-full bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg">
                  Book a Test
                </Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <MicahAssistant />

      {/* Footer */}
      <footer className="bg-[#0A2540] text-white mt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-white rounded-xl p-2 inline-flex">
                <img src="/mbs-logo.png" alt="Modern Business Solutions logo" className="h-12 w-auto" />
              </div>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              The official TOEIC® Testing Center for the Cayman Islands. English language
              testing, custom app development, and modern accounting software.
            </p>
          </div>
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider text-[#39C1D0] mb-4">Quick Links</h4>
            <ul className="space-y-2.5 text-sm text-slate-300">
              <li><Link to="/book" className="hover:text-white transition-colors">Book a Test</Link></li>
              <li><Link to="/services" className="hover:text-white transition-colors">Testing Services</Link></li>
              <li><Link to="/scores" className="hover:text-white transition-colors">Understanding Test Scores</Link></li>
              <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
              <li><Link to="/my-booking" className="hover:text-white transition-colors">Find My Booking</Link></li>
              <li><Link to="/faqs" className="hover:text-white transition-colors">FAQs</Link></li>
              <li>
                <a
                  href="https://edtoeic.engdis.com/login.aspx?"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                  data-testid="footer-olpc-link"
                >
                  Online Learning &amp; Preparation Course: TOEIC
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider text-[#39C1D0] mb-4">Solutions</h4>
            <ul className="space-y-2.5 text-sm text-slate-300">
              <li><Link to="/app-development" className="hover:text-white transition-colors">App Development</Link></li>
              <li><Link to="/blue-ledger" className="hover:text-white transition-colors">Blue Ledger Accounting</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
              <li><Link to="/admin" className="hover:text-white transition-colors">Staff Admin</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider text-[#39C1D0] mb-4">Visit Us</h4>
            <ul className="space-y-3 text-sm text-slate-300">
              <li className="flex items-start gap-2.5"><MapPin size={16} className="text-[#39C1D0] mt-0.5 shrink-0" /> {SITE.address}<br />{SITE.addressNote}</li>
              <li className="flex items-center gap-2.5"><Phone size={16} className="text-[#39C1D0] shrink-0" /> {SITE.phone}</li>
              <li className="flex items-center gap-2.5"><Mail size={16} className="text-[#39C1D0] shrink-0" /> {SITE.email}</li>
              <li className="flex items-center gap-2.5"><Clock size={16} className="text-[#39C1D0] shrink-0" /> {SITE.hours}</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-400">
            <span>© {new Date().getFullYear()} Modern Business Solutions. All rights reserved.</span>
            <span>TOEIC® is a registered trademark of ETS.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
